import { App } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { z } from "zod";
import { parseNativeLevel } from "./nativeLevel";

/**
 * Self-hosted over-the-air web bundle updates for the iOS app.
 *
 * Every Cloudflare Pages production deploy publishes dist/ota/manifest.json plus a zip
 * of the web bundle (client/scripts/build-ota.mjs). On launch and on resume the
 * app fetches the manifest; if it describes a different bundle than the one
 * running, it downloads it and schedules it to apply after the app has been
 * backgrounded for a while (or killed). Only HTML/CSS/JS changes travel this
 * way — native changes still need a new TestFlight/App Store build (see
 * docs/ios-release.md).
 */

const OTA_MANIFEST_URL =
  import.meta.env.VITE_OTA_MANIFEST_URL ??
  "https://slater-cleaning.pages.dev/ota/manifest.json";

// Don't swap the UI out from under a quick app switch.
const APPLY_AFTER_BACKGROUND_MS = 5 * 60 * 1000;

const FAILED_VERSIONS_KEY = "ota.failedVersions";
const BUILTIN_BUILT_AT_KEY = "ota.builtinBuiltAt";

const ZOtaManifest = z.object({
  version: z.string().min(1),
  builtAt: z.number().int(),
  url: z.string().min(1),
  checksum: z.string().min(1),
  minNativeBuild: z.number().int(),
});

const ZFailedVersions = z.array(z.string());

async function getFailedVersions(): Promise<string[]> {
  const { value } = await Preferences.get({ key: FAILED_VERSIONS_KEY });
  if (!value) return [];
  const parsed = ZFailedVersions.safeParse(JSON.parse(value));
  return parsed.success ? parsed.data : [];
}

async function recordFailedVersion(version: string) {
  const failed = await getFailedVersions();
  if (failed.includes(version)) return;
  // Keep the list short; versions are unique per deploy.
  const next = [...failed, version].slice(-20);
  await Preferences.set({ key: FAILED_VERSIONS_KEY, value: JSON.stringify(next) });
}

/**
 * Build time of the web bundle embedded in the installed binary. Recorded the
 * first time the binary runs its built-in bundle (the updater resets to it
 * after every binary update), so a deploy older than the binary's own web code
 * is never applied — e.g. a TestFlight build made from a branch ahead of main.
 * Older deploys *newer* than that stay installable, which is what makes
 * a rollback in Cloudflare Pages (Deployments → "Rollback to this deployment") work.
 */
async function getBuiltinBuiltAt(): Promise<number> {
  const { bundle } = await CapacitorUpdater.current();
  if (bundle.id === "builtin") {
    const builtAt = import.meta.env.VITE_WEB_BUNDLE_BUILT_AT;
    await Preferences.set({ key: BUILTIN_BUILT_AT_KEY, value: String(builtAt) });
    return builtAt;
  }
  const { value } = await Preferences.get({ key: BUILTIN_BUILT_AT_KEY });
  return value ? Number(value) : 0;
}

let checking = false;

async function checkForUpdate() {
  if (checking) return;
  checking = true;
  try {
    const response = await fetch(OTA_MANIFEST_URL, { cache: "no-store" });
    if (!response.ok) return;
    const manifest = ZOtaManifest.parse(await response.json());

    if (manifest.version === import.meta.env.VITE_WEB_BUNDLE_VERSION) return;
    if (manifest.builtAt < (await getBuiltinBuiltAt())) return;
    // A bundle that failed to boot was rolled back; don't retry it until a new deploy.
    if ((await getFailedVersions()).includes(manifest.version)) return;

    // Bundles built for newer native code (new plugins, permissions) must not
    // load in an older binary.
    const [{ build }, builtin] = await Promise.all([
      App.getInfo(),
      CapacitorUpdater.getBuiltinVersion(),
    ]);
    if (parseNativeLevel(builtin.version, build) < manifest.minNativeBuild) return;

    const { bundles } = await CapacitorUpdater.list();
    const existing = bundles.find(
      (bundle) => bundle.version === manifest.version && bundle.status !== "deleted"
    );
    if (existing?.status === "error") {
      await recordFailedVersion(manifest.version);
      return;
    }
    const bundle =
      existing && (existing.status === "success" || existing.status === "pending")
        ? existing
        : await CapacitorUpdater.download({
            url: new URL(manifest.url, OTA_MANIFEST_URL).toString(),
            version: manifest.version,
            checksum: manifest.checksum,
          });
    await CapacitorUpdater.next({ id: bundle.id });
    await CapacitorUpdater.setMultiDelay({
      conditionMode: "or",
      delayConditions: [
        { kind: "kill" },
        { kind: "background", value: String(APPLY_AFTER_BACKGROUND_MS) },
      ],
    });
  } catch (error) {
    console.error("OTA update check failed:", error);
  } finally {
    checking = false;
  }
}

/** Starts update checks. Call once at startup, before React renders. */
export function initOtaUpdates() {
  CapacitorUpdater.addListener("updateFailed", ({ bundle }) => {
    recordFailedVersion(bundle.version).catch(() => undefined);
  });
  // The rollback may have happened before this listener existed (previous launch).
  CapacitorUpdater.getFailedUpdate()
    .then((failed) => (failed ? recordFailedVersion(failed.bundle.version) : undefined))
    .catch(() => undefined)
    .finally(() => {
      checkForUpdate();
    });
  App.addListener("resume", () => {
    checkForUpdate();
  });
}

/**
 * Confirms this bundle booted. Call after React's first successful commit; if
 * it isn't called within appReadyTimeout (capacitor.config.ts) the updater
 * rolls back to the previous bundle.
 */
export function markBundleHealthy() {
  CapacitorUpdater.notifyAppReady().catch((error) =>
    console.error("notifyAppReady failed:", error)
  );
}
